import { CITY_LEG_KM, FOV_TAN_HALF, MIN_ALTITUDE, MOTION_FRACTION, SHORT_LEG_DROP, SHORT_LEG_KM, STOP_VIEW_KM } from "./reelConstants";

/**
 * How the camera's height is planned across a whole trip, in one place.
 *
 * This used to be spread over three files that each derived it slightly differently, which is how a
 * stop could arrive at one height and leave at another. It is pure arithmetic on the reel data, so
 * both the component and `calculateMetaData` can call it and get the same answer -- they have to,
 * because the plan decides how long a leg lasts and therefore how long the film is.
 */

/** Extra room around a leg so the pins are not flush against the edge of the frame. */
const FRAME_MARGIN = 1.35;

/** A city leg may rise half again over its own height; the hop should read as a hop, not a flight. */
const CITY_ARC_RISE = 1.15;

/**
 * The ceiling on how fast the camera may change height, in zoom levels per second -- a doubling of
 * altitude is one level. This is the number that decides whether a leg feels calm: measured on the
 * Bangkok crawl the settled legs run 0.3 to 1.0, while the flight out to Koh Phangan hit 13.9, and
 * that is exactly the leg that read as broken -- those two figures are speed-independent observations
 * about the trip, not about any one rung of the ladder below. Tied to the speed setting, so the
 * ceiling itself stays the user's choice rather than a constant only we can see.
 */
//
// Where the rung sits decides how long a film is, because every stop is capped at a place-sized view
// (STOP_VIEW_KM) and a long leg has to climb from there to its own framing height and back inside
// the leg: 773 km Almere to Dublin is 6.3 zoom levels up and down again. At the old distance-only
// timing (2 s) that peaks at 9.7 levels per second, which is the whip-zoom this ceiling exists to
// stop; at 3 it stretched the leg to 6.6 s and the film to twice its former length. 5 holds the
// 773 km leg to about four seconds and keeps city legs on their distance timing, where the climb is small.
export const maxZoomPerSecond = (speed?: string) =>
	({ superfast: 10, fast: 7.5, medium: 5, slow: 3.3, superslow: 2.3 } as Record<string, number>)[speed || "medium"] ?? 5;

/**
 * The bbox diagonal of a leg in kilometres -- how much ground it has to fit on screen. A winding
 * road covers far less ground than its own length, so this is the number that decides framing.
 * Haversine rather than a flat approximation, because the same figure is asked of a bar hop and of
 * Bangkok to Los Angeles.
 */
export const extentKm = (coords?: number[][]) => {
	if (!Array.isArray(coords) || coords.length < 2) return 0;
	const lngs = coords.map((c) => c[0]);
	const lats = coords.map((c) => c[1]);
	const [lng1, lat1, lng2, lat2] = [Math.min(...lngs), Math.min(...lats), Math.max(...lngs), Math.max(...lats)];
	const R = 6371.0088;
	const [p1, p2] = [(lat1 * Math.PI) / 180, (lat2 * Math.PI) / 180];
	const dPhi = p2 - p1;
	const dLambda = ((lng2 - lng1) * Math.PI) / 180;
	const a = Math.sin(dPhi / 2) ** 2 + Math.cos(p1) * Math.cos(p2) * Math.sin(dLambda / 2) ** 2;
	return 2 * R * Math.asin(Math.min(1, Math.sqrt(a)));
};

/**
 * Work back from the optics rather than from a table of thresholds. A camera at altitude A sees
 * `2 * A * tan(fov/2) / cos(pitch)` of ground; invert that for the span a leg occupies and the film
 * sizes itself -- bars a kilometre apart land at street level, an island loop at island level.
 */
export const spanAltitude = (spanKm: number, pitchDeg: number, fovTanHalf: number) =>
	Math.max(MIN_ALTITUDE, Math.min(8500000, spanKm * 1000 * (Math.cos((pitchDeg * Math.PI) / 180) / (2 * fovTanHalf)) * FRAME_MARGIN));

export type LegPlan = {
	/** The leg's own framing height -- high enough to see the whole leg at once. */
	own: number;
	/** Where the camera sits at the stop this leg leaves from. */
	entry: number;
	/** The top of the leg's arc. */
	peak: number;
	/** Where the camera sits at the stop this leg arrives at. */
	exit: number;
	/** Frames this leg lasts: never fewer than the distance asks for, nor than the climb needs. */
	frames: number;
	/** Zoom levels per second the plan actually ends up at -- what the ceiling is enforced against. */
	zoomPerSecond: number;
};

export const planLegs = (
	reelData: any[],
	opts: { baseFrames: number[]; pitchDeg: number; fovTanHalf: number; fps: number; speed?: string; zoom?: number }
): LegPlan[] => {
	const { baseFrames, pitchDeg, fovTanHalf, fps, speed } = opts;
	// How much closer than the framing the camera flies: 1 is the framing, 2 is half as high. Every
	// height in the plan divides by it -- the floor too, which a bar crawl sits on the whole way -- and
	// nothing else reads a height from anywhere but this plan, so one division moves the whole film.
	// Heights are above sea level, not above the ground: in a hilly old town a large zoom puts the
	// camera into the hill. Keep it for flat places.
	const zoomIn = opts.zoom && opts.zoom > 0 ? opts.zoom : 1;
	// A destination's path is the leg it leaves on, so the last destination has none -- but the render
	// worker gives it a two-point stand-in for its coordinate checks, and read as a leg that stand-in
	// framed the final stop at 283 m (the floor) where the editor, whose data has no stand-in, closes
	// on a place-sized view. The film ended a hundred metres up in production and a few kilometres up
	// in the preview, and the leg into that stop was stretched for a descent the preview never made.
	const lastIndex = (reelData?.length ?? 0) - 1;
	const kms = (reelData || []).map((d: any, i: number) => (i === lastIndex ? 0 : extentKm(d?.path)));
	const present = kms.filter((km) => km > 0);

	// On a trip whose legs are all short, per-leg heights alone make the camera re-seek at every stop:
	// on the Koh Phangan loop the legs run 7.5 to 29.8 km, so the height wanted to swing by a factor
	// of three and every arrival ended with a visible pull-back. Blend halfway to the trip's own mean.
	const local = present.length > 0 && present.every((km) => km < CITY_LEG_KM);
	const mean = present.length ? present.reduce((sum, km) => sum + spanAltitude(km, pitchDeg, fovTanHalf), 0) / present.length : 0;
	const own = kms.map((km) => {
		if (km <= 0) return 0;
		const a = spanAltitude(km, pitchDeg, fovTanHalf);
		const framed = local ? (a + mean) / 2 : a;
		// A short leg drops below its own framing height. Fitting the whole route on screen is the
		// right call for a flight and the wrong one for an island hop, where it puts the camera so far
		// up that the hills flatten out. The motion floor still applies underneath this.
		// The 1 km floor has to survive the drop, or a 1 km bar hop ends up at 550 m -- below the
		// towers it is meant to be flying over.
		return km < SHORT_LEG_KM ? Math.max(MIN_ALTITUDE, framed * SHORT_LEG_DROP) : framed;
	});

	// A stop takes the shallower of the two legs it joins: descend into a place, sit still, climb back
	// out in flight. The two ends of the film have one leg each, so they fall back to a stop-sized
	// view -- the film opens by descending to a place and closes the same way.
	const stopCeiling = spanAltitude(STOP_VIEW_KM, pitchDeg, fovTanHalf);
	const stopAt = (i: number) => {
		const arriving = own[i - 1] ?? 0;
		const departing = own[i] ?? 0;
		if (!arriving && !departing) return 0;
		// Every stop is capped, not just the two ends. Inheriting a leg's height shows the region the
		// route crosses instead of the place it stops at.
		if (!arriving) return Math.min(departing, stopCeiling);
		if (!departing) return Math.min(arriving, stopCeiling);
		return Math.min(arriving, departing, stopCeiling);
	};

	const ceiling = maxZoomPerSecond(speed);
	return own.map((legOwn, i) => {
		const base = baseFrames[i] || 0;
		const entry = stopAt(i);
		const exit = stopAt(i + 1);
		if (!legOwn || !entry || !exit || !base) {
			return { own: legOwn / zoomIn, entry: entry / zoomIn, exit: exit / zoomIn, peak: legOwn / zoomIn, frames: base, zoomPerSecond: 0 };
		}
		// The peak is the leg's own framing height -- high enough to see the leg, and nothing beyond
		// that. It also has to clear both ends, or the "arc" is a ramp with a kink in the middle.
		const peak = Math.max(entry, exit, legOwn * (kms[i] < CITY_LEG_KM ? CITY_ARC_RISE : 1));
		const zoom = arcPeakZoom(entry, peak, exit);
		const frames = Math.max(base, Math.ceil((zoom / ceiling) * fps));
		// Scaling every height by the same factor leaves the ratios, and so the zoom rate and the frames, as they were.
		return { own: legOwn / zoomIn, entry: entry / zoomIn, peak: peak / zoomIn, exit: exit / zoomIn, frames, zoomPerSecond: frames > 0 ? zoom / (frames / fps) : 0 };
	});
};

/** Cubic in-out: nought at both ends, so nothing starts or stops with a jolt. */
const easeInOut = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

/**
 * The camera's height along a leg, as a fraction of the way through it.
 *
 * This used to be two straight lines in log space meeting at the peak, which gave the curve three
 * places where the vertical speed changed abruptly: nought to full climb at the moment of departure,
 * a corner at the top, and full descent to nought on arrival. The heights were all continuous, so
 * nothing looked wrong in the numbers -- it just read as being suddenly airborne.
 *
 * Now it is one smooth curve: an eased travel from the departing stop's height to the arriving one,
 * plus a hump that lifts it over the leg. `sin^2` is flat at nought, a half and one, so the climb
 * eases in, the apex has no corner, and the descent settles rather than stops.
 */
export const arcAltitude = (phase: number, entry: number, peak: number, exit: number) => {
	const t = Math.max(0, Math.min(1, phase));
	const lg = (x: number) => Math.log(Math.max(1, x));
	const base = lg(entry) + (lg(exit) - lg(entry)) * easeInOut(t);
	const lift = Math.max(0, lg(peak) - Math.max(lg(entry), lg(exit)));
	return Math.exp(base + lift * Math.sin(Math.PI * t) ** 2);
};

/**
 * The steepest the arc ever gets, in zoom levels per unit of the leg. Pacing has to answer to the
 * fastest moment rather than the average, or an eased curve simply hides its rush in the middle.
 */
export const arcPeakZoom = (entry: number, peak: number, exit: number, samples = 240) => {
	let steepest = 0;
	let prev = Math.log2(arcAltitude(0, entry, peak, exit));
	for (let i = 1; i <= samples; i++) {
		const here = Math.log2(arcAltitude(i / samples, entry, peak, exit));
		steepest = Math.max(steepest, Math.abs(here - prev) * samples);
		prev = here;
	}
	return steepest;
};

/**
 * How high the camera has to be to travel `kmPerFrame` without outrunning the map.
 *
 * The same optics as `spanAltitude`, asked a different question: instead of "how high to see this
 * leg", it is "how high to see where I will be next frame". Below this the view changes completely
 * between frames, and Mapbox is still fetching the previous frame's tiles when the next one is
 * captured -- which renders as an empty background, not as a blurry one.
 */
export const motionFloor = (kmPerFrame: number, pitchDeg: number) =>
	kmPerFrame > 0 ? spanAltitude(kmPerFrame / MOTION_FRACTION, pitchDeg, FOV_TAN_HALF) : 0;

/** Progress along a leg. Flat at both ends so a departure builds up and an arrival settles. */
export const legProgress = (phase: number) => easeInOut(Math.max(0, Math.min(1, phase)));
